import { useEffect, useSyncExternalStore } from 'react';

// The bottom of the viewport is shared by several independent floating
// widgets: the feedback button and the upload manager both want the
// bottom-right corner, the download manager the bottom-left, and the basket
// bar spans the middle. None of them knows about the others, so each one
// reports the space it occupies here and the ones that can yield read it back.

const GAP = 16;

// Vertical space the feedback button needs above whatever it is stacked on. A
// constant, not a measurement: a panel sized off the button's reported box
// would feed straight back into the button's own offset.
export const FEEDBACK_BUTTON_RESERVE = 88;

type OverlaySide = 'left' | 'right' | 'center';
type OverlayId =
  | 'feedbackButton'
  | 'uploadManager'
  | 'downloadManager'
  | 'basketBar';

type Overlay = {
  side: OverlaySide;
  /** Space taken from its own side edge, including the gap after it. */
  width: number;
  /** Space taken from the bottom edge, including the gap above it. */
  height: number;
};

type Overlays = Partial<Record<OverlayId, Overlay>>;

const EMPTY: Overlays = {};

let snapshot: Overlays = EMPTY;
const listeners = new Set<() => void>();

const publish = (id: OverlayId, next: Overlay | null) => {
  const current = snapshot[id];
  if (
    next === null
      ? current === undefined
      : current &&
        current.side === next.side &&
        current.width === next.width &&
        current.height === next.height
  ) {
    return;
  }

  const updated = { ...snapshot };
  if (next === null) {
    delete updated[id];
  } else {
    updated[id] = next;
  }
  snapshot = updated;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => snapshot;
const getServerSnapshot = () => EMPTY;

/**
 * Publishes the space `element` occupies while it is mounted; a hidden element
 * (`display: none`) measures 0 and reserves nothing. Pass the node from state,
 * not a ref, so this re-runs when it attaches.
 */
export const useReportBottomOverlay = (
  id: OverlayId,
  side: OverlaySide,
  element: HTMLElement | null,
) => {
  useEffect(() => {
    if (!element) return;

    const observer = new ResizeObserver(() => {
      const rect = element.getBoundingClientRect();
      // Hidden, or not laid out yet - either way it takes no space.
      if (rect.width === 0 || rect.height === 0) {
        publish(id, null);
        return;
      }

      publish(id, {
        side,
        width:
          (side === 'right'
            ? window.innerWidth - rect.left
            : rect.right) + GAP,
        height: window.innerHeight - rect.top + GAP,
      });
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      publish(id, null);
    };
  }, [id, side, element]);
};

const useOverlays = () =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

const reduce = (
  overlays: Overlays,
  side: OverlaySide,
  ignore: OverlayId[],
  pick: (overlay: Overlay) => number,
) =>
  (Object.keys(overlays) as OverlayId[])
    .filter((id) => !ignore.includes(id))
    .map((id) => overlays[id])
    .filter((overlay) => overlay?.side === side)
    .reduce((widest, overlay) => Math.max(widest, pick(overlay!)), 0);

/** Width to keep clear on `side`, or 0 when nothing is docked there. */
export const useSideClearance = (
  side: OverlaySide,
  ignore: OverlayId[] = [],
) => reduce(useOverlays(), side, ignore, (overlay) => overlay.width);

/** Height to keep clear above the bottom edge on `side`. */
export const useBottomClearance = (
  side: OverlaySide,
  ignore: OverlayId[] = [],
) => reduce(useOverlays(), side, ignore, (overlay) => overlay.height);

// Height of a single-row basket bar plus its bottom gap, used until the real
// one is measured.
const BASKET_BAR_FALLBACK = 96;

/**
 * Bottom padding a page hosting the fixed basket bar must reserve to keep its
 * last row and its infinite-scroll trigger clear of it. Measured, because a
 * manager docked in a corner narrows the bar until its buttons wrap onto two
 * or three rows.
 */
export const useFixedBasketBarClearance = () =>
  useBottomClearance('center') || BASKET_BAR_FALLBACK;
